import React, { useEffect, useState, useRef } from 'react';
import { TextField, Button, Grid, Paper, Typography, Autocomplete } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/api';
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';

export default function HotelBookingRequestForm() {

    const { user, token } = useAuth();
    const navigate = useNavigate();
  
    const [managers, setManagers] = useState([]);
  
    useEffect(() => {
      if (user) {
        const tempValues = { ...formValue }
        tempValues.empId = user.employee_code;
        tempValues.empName = user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
        setFormValue(tempValues);
      }
    }, [user]);
  
    const [formValue, setFormValue] = useState({
      empId: '',
      empName: '',
      location: '',
      checkInDate: '',
      checkInTime: '',
      checkOutDate: '',
      checkOutTime: '',
      noOfPerson: '',
      noOfRooms: '',
      approvalManager: '',
    });
  
    const handleInputChange = (event) => {
      const { name, value } = event.target;
      setFormValue((prevValues) => ({
        ...prevValues,
        [name]: value
      }));
    }
  
    const fromLocationRef = useRef(null);
    const toLocationRef = useRef(null);
    const fromLocationAutocomplete = useRef(null);
    const toLocationAutocomplete = useRef(null);
  
    useEffect(() => {
      getManagers();
      fromAndToLocationAutoComplete();
    },
      []);
  
    const getManagers = () => {
      api.get('/api/Users/get_managers')
        .then(response => {
          setManagers(response.data);
        })
        .catch(error => {
          console.error('Error fetching managers', error);
        });
    }
  
    const fromAndToLocationAutoComplete = () => {
      fromLocationAutocomplete.current = new window.google.maps.places.Autocomplete(fromLocationRef.current);
      toLocationAutocomplete.current = new window.google.maps.places.Autocomplete(toLocationRef.current);
  
      fromLocationAutocomplete.current.addListener('place_changed', () => {
        const place = fromLocationAutocomplete.current.getPlace();
        setFormValue((prevValues) => ({
          ...prevValues,
          location: place.name + ", " + place.formatted_address,
        }));
        calculateDistance();
      });
  
      toLocationAutocomplete.current.addListener('place_changed', () => {
        const place = toLocationAutocomplete.current.getPlace();
        setFormValue((prevValues) => ({
          ...prevValues,
          toLocation: place.name + ", " + place.formatted_address,
        }));
        calculateDistance();
      });
    }
  
    const calculateDistance = () => {
      if (!fromLocationAutocomplete.current.getPlace() || !toLocationAutocomplete.current.getPlace()) return;
  
      const origin = fromLocationAutocomplete.current.getPlace().formatted_address;
      const destination = toLocationAutocomplete.current.getPlace().formatted_address;
  
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status === 'OK') {
            const distanceValue = response.rows[0].elements[0].distance.value / 1000; // Convert meters to kilometers
            setFormValue((prevValues) => ({
              ...prevValues,
              tentativeKm: Math.round(distanceValue),
            }));
          } else {
            console.error(`Error calculating distance: ${status}`);
          }
        }
      );
    };
  
    const handleDateChange = (date, name) => {
      setFormValue({ ...formValue, [name]: date });
    };
  
    const handleTimeChange = (time, name) => {
      setFormValue({ ...formValue, [name]: time });
    };
  
    const handleSubmit = (e) => {
      e.preventDefault();
  
      const requestData = {
        employeeId: +user["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
        approvalManagerId: formValue.approvalManager,
        location: formValue.location,
        checkInDate: formValue.checkInDate,
        checkInTime: formValue.checkInTime,
        checkOutDate: formValue.checkOutDate,
        checkOutTime: formValue.checkOutTime,
        noOfPerson: +formValue.numberOfPerson,
        noOfRooms: +formValue.numberOfRooms,
      };
      api.post('/api/HotelBookingRequest', requestData)
      .then(response => {
        navigate('/hotel-request')
      })
      .catch(error => {
        console.error('Error submitting request', error);
        // Handle error
      });
    }
  
    return (
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 1200,
          margin: 'auto'
        }}
      >
        <Typography marginBottom={2} component="p" variant="h5">New Hotel Booking Request</Typography>
        <form sx={{ top: 200 }} onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Emp. ID"
                name="empId"
                value={formValue.empId}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Emp Name"
                name="empName"
                value={formValue.empName}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location"
                name="location"
                value={formValue.location}
                onChange={handleInputChange}
                inputProps={{ required: true}}
                inputRef={fromLocationRef}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="No. of person"
                name="numberOfPerson"
                type="number"
                inputProps={{ min: 1, required: true }}
                onWheel={(e) => e.target.blur()}
                value={formValue.numberOfPerson}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>    
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <Grid item xs={12} sm={3} xl={3}>
                <DatePicker sx={{ width: '100%' }} disablePast inputProps={{ required: true }} onChange={(checkInDate) => handleDateChange(checkInDate, 'checkInDate')} label="Check In Date" fullWidth />
              </Grid>
              <Grid item xs={12} sm={3} xl={3}>
                <TimePicker sx={{ width: '100%' }} inputProps={{ required: true }} onChange={(time) => handleTimeChange(time, 'checkInTime')} label="Check In Time" ampm fullWidth />
              </Grid>
              <Grid item xs={12} sm={3} xl={3}>
                <DatePicker sx={{ width: '100%' }} disablePast inputProps={{ required: true }} onChange={(checkOutDate) => handleDateChange(checkOutDate, 'checkOutDate')} label="Check Out Date" fullWidth />
              </Grid>
              <Grid item xs={12} sm={3} xl={3}>
                <TimePicker sx={{ width: '100%' }} inputProps={{ required: true }} onChange={(time) => handleTimeChange(time, 'checkOutTime')} label="Check Out Time" ampm fullWidth />
              </Grid>
            </LocalizationProvider>
            <Grid item xs={12} sm={6}>
              <TextField
                label="No. of Rooms"
                name="numberOfRooms"
                type="number"
                inputProps={{ min: 1, required: true }}
                onWheel={(e) => e.target.blur()}
                value={formValue.numberOfRooms}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disablePortal
                options={managers}
                getOptionLabel={(manager) => manager.name}
                getOptionKey={(manager) => manager.id}
                onChange={(event, value) => setFormValue((prevValues) => ({
                  ...prevValues,
                  approvalManager: value?.id || ''
                }))}
                value={managers.find(manager => manager.id === formValue.approvalManager) || null}
                sx={{ width: '100%' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Approval Manager"
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
  
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ marginTop: 4 }}>
            Submit
          </Button>
        </form>
      </Paper>
    );
}